import React, { FC, useState } from 'react'
import { includes } from 'lodash'

import { AppErrorMessage } from 'components/Common/AppFormFields/AppTextField/AppTextField.component'
import {
  AppMenuContainerWrapper,
  AppMenuInputWrapper,
  AppMenuWrapper,
} from 'components/Common/AppMenu/AppMenu.style'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { AppChevronDownIcon } from 'components/Common/AppSvgIcons/AppChevronDownIcon'
import appColors from 'appColors'
import { useClickOutside } from 'hooks/AppuseClickOutside/useClickOutside.hook'
import { AppCheckBox } from '../AppFormFields/AppCheckBox/AppCheckBox.component'
import { ColorType } from 'appUtils/colorUtils'

interface IAppMenuProps {
  value: string[]
  options: {
    value: string
    label: string
    description?: string
    avatar?: string // URL for avatar image
    initials?: string
    color?: ColorType
  }[]
  placeholder: string
  onChange: (input: string[]) => void
  isDisabled?: boolean
  id?: string
  error?: string
  disableSearch?: boolean
  isSelectedAll?: boolean
}
export const AppMenu: FC<IAppMenuProps> = ({
  value,
  options,
  onChange,
  placeholder,
  isDisabled = false,
  error,
  disableSearch = false,
  isSelectedAll = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // Handle clicks outside the menu to close it
  const divRef = useClickOutside(() => setIsMenuOpen(false))

  const handleSelect = (optionValue: string) => {
    if (isDisabled) return

    const currentValue = value ?? []
    const individualOptions = options.filter((opt) => opt.value !== 'allMyOptions')

    // Handle "All My Options" selection
    if (optionValue === 'allMyOptions') {
      const allIndividualValues = individualOptions.map((opt) => opt.value)
      const hasAllSelected = allIndividualValues.every((val) => currentValue.includes(val))

      if (hasAllSelected && currentValue.includes('allMyOptions')) {
        // If all are selected including "All My Options", deselect all
        onChange([])
      } else {
        // Select all individual options + "All My Options"
        onChange(['allMyOptions', ...allIndividualValues])
      }
      return
    }

    // Handle individual option selection
    const isSelected = currentValue.includes(optionValue)
    let newValue: string[]

    if (isSelected) {
      // Deselect this option and remove "All My Options" if it was selected
      newValue = currentValue.filter((item) => item !== optionValue && item !== 'allMyOptions')
    } else {
      // Select this option and remove "All My Options" from current selection first
      const withoutAllMyOptions = currentValue.filter((item) => item !== 'allMyOptions')
      newValue = [...withoutAllMyOptions, optionValue]

      // Check if all individual options are now selected
      const allIndividualValues = individualOptions.map((opt) => opt.value)
      const allSelected = allIndividualValues.every((val) => newValue.includes(val))

      if (allSelected) {
        // Add "All My Options" back since all individual items are selected
        newValue = ['allMyOptions', ...newValue]
      }
    }

    onChange(newValue)
  }

  const handleToggle = (event) => {
    if (!isDisabled) {
      event.stopPropagation()
      setIsMenuOpen(!isMenuOpen)
    }
  }

  const filteredOptions = options.filter((option) =>
    option.label?.toLowerCase()?.includes(searchTerm.toLowerCase())
  )

  // Filter selected options, excluding 'allMyOptions' from display if individual items are also selected
  const selectedOptions = options.filter((option) => {
    const isSelected = includes(Array.isArray(value) ? value : [value], option.value)
    // If 'allMyOptions' is selected along with individual items, don't show 'allMyOptions' in the display
    if (option.value === 'allMyOptions' && value.length > 1) {
      return false
    }
    return isSelected
  })

  return (
    <div ref={divRef} className="relative">
      <div
        className={` px-4 py-2 rounded-md flex items-center gap-2 w-full cursor-pointer `}
        onClick={handleToggle}
      >
        {selectedOptions.length > 0 ? (
          <div className="w-full">
            <div className="flex flex-row items-center gap-2 flex-wrap">
              {selectedOptions.map((option, index) => (
                <div
                  key={index}
                  className="flex flex-row items-center gap-2 px-2 py-1 bg-gray-100 rounded-md"
                >
                  {option.initials && (
                    <AppAvatar
                      size={'sm'}
                      type={'text'}
                      shape={'Rounded'}
                      color={option.color}
                      text={option.initials}
                    />
                  )}
                  <span className="text-gray-800 text-sm whitespace-nowrap">{option.label}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="w-full">
            <div className="w-full text-app-grey-60 flex items-center justify-between">
              <div className="min-h-[1.5rem]"></div>
              <AppChevronDownIcon color={appColors['app-grey']['60']} />
            </div>
          </div>
        )}
      </div>

      {/* Dropdown menu */}
      {isMenuOpen && (
        <AppMenuWrapper className=" !bg-app-white-100 mt-2 bg-white border border-gray-300 rounded-md shadow-lg w-full absolute z-50">
          {!disableSearch && (
            <AppMenuInputWrapper
              type="text"
              disabled={disableSearch}
              placeholder="Search name or group"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border-b border-gray-300 w-full"
            />
          )}

          {filteredOptions.map((option, index) => {
            const isSelected = includes(Array.isArray(value) ? value : [value], option.value)
            return (
              <AppMenuContainerWrapper
                key={index}
                isSelected={isSelected}
                onClick={(e) => {
                  e.stopPropagation()
                  handleSelect(option.value)
                }}
                className={`flex items-center gap-4 p-4 cursor-pointer ${
                  isSelected ? 'bg-gray-200' : 'hover:bg-gray-100'
                }`}
              >
                <AppCheckBox value={isSelected} onChange={() => handleSelect(option.value)} />
                {option.initials && (
                  <AppAvatar
                    size={'sm'}
                    type={'text'}
                    shape={'Rounded'}
                    color={option.color}
                    text={option.initials}
                  />
                )}
                <div className="flex flex-col">
                  <span
                    className={`font-medium whitespace-nowrap ${
                      isSelected ? 'text-black' : 'text-gray-800'
                    } `}
                  >
                    {option.label}
                  </span>
                  {option.description && (
                    <span className="text-sm text-gray-500 whitespace-nowrap">
                      {option.description}
                    </span>
                  )}
                </div>
              </AppMenuContainerWrapper>
            )
          })}

          {error && <AppErrorMessage error={error} />}
        </AppMenuWrapper>
      )}
    </div>
  )
}
